import React, { useState, ChangeEvent, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  SelectChangeEvent,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Chip,
} from "@mui/material";
import Accordion from "react-bootstrap/Accordion";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { fetchBookOptions, fetchBrokerNameOptions, fetchClearerName, StrategyOptions } from "../Trading/PaperTrading/FecthApis";
// import { mockCostTypeData } from "../CostType/TableCostType";

// ─── Type definitions ─────────────────────────────────────────────────────────
interface OptionType {
  value: string;
  label: string;
}

interface TouchedType {
  [key: string]: boolean;
}

interface ErrorsType {
  [key: string]: string;
}

interface FormDataType {
  [key: string]: string;
  type: string;
  costType: string;
  amount: string;
  currency: string;
  dueDate: string;
  book: string;
  trader: string;
  strategy: string;
  clearer: string;
  broker: string;
  product: string;
  counterparty: string;
  requiresSettlement: string;
  excludeFromPNL: string;
  description: string;
  status: string;
}

interface CompactSelectProps {
  label: string;
  options: OptionType[];
  value: string;
  onChange: (event: SelectChangeEvent) => void;
  disabled?: boolean;
  sx?: object;
  slotProps?: { inputLabel?: { shrink?: boolean } };
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
}

interface CompactTextFieldProps {
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  disabled?: boolean;
  sx?: object;
  slotProps?: { inputLabel?: { shrink?: boolean } };
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
}

const MOCK_TRADERS: OptionType[] = [
  { value: "developer", label: "Developer" },
  { value: "tester", label: "Tester" },
  { value: "director", label: "Director" },
  { value: "manager", label: "Manager" },
];
const MOCK_STRATEGIES: OptionType[] = [
  { value: "1000", label: "1000" },
  { value: "1001", label: "1001" },
  { value: "2000", label: "2000" },
];
const MOCK_CLEARERS: OptionType[] = [
  { value: "RJO", label: "RJO" },
  { value: "ICE", label: "ICE" },
  { value: "CME", label: "CME" },
];
const MOCK_BROKERS: OptionType[] = [
  { value: "BGM", label: "BGM" },
  { value: "GFI", label: "GFI" },
  { value: "MAREX", label: "Marex" },
];
const MOCK_PRODUCTS: OptionType[] = [
  { value: "brent", label: "Brent" },
  { value: "wti", label: "WTI" },
  { value: "gasoil", label: "Gasoil" },
  { value: "naphtha", label: "Naphtha" },
];
const MOCK_COUNTERPARTIES: OptionType[] = [
  { value: "BGM", label: "BGM" },
  { value: "GFI", label: "GFI" },
  { value: "VITOL", label: "Vitol" },
  { value: "SHELL", label: "Shell" },
];
const MOCK_CURRENCIES: OptionType[] = [
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
  { value: "AED", label: "AED" },
];
const COST_TYPES: OptionType[] = [
  { value: "risk_reserve", label: "Risk Reserve" },
  { value: "brokerage_adjustment", label: "Brokerage Adjustment" },
  { value: "fee", label: "Fee" },
  { value: "margin", label: "Margin" },
  { value: "other", label: "Other" },
];
const STATUSES: OptionType[] = [
  { value: "pending", label: "Pending" },
  { value: "committed", label: "Committed" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const compactFieldSx = {
  flex: {
    xs: "1 1 auto",
    sm: "1 1 25%",
    md: "1 1 25%",
    lg: "1 1 25%",
    xl: "1 1 12%",
  },
  "& .MuiInputBase-root": {
    fontSize: "0.7rem",
    height: "32px",
    fontWeight: "bold",
    marginBottom: "0.5rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.9rem",
    transform: "translate(14px, 8px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -8px) scale(0.74)",
      backgroundColor: "white",
      paddingRight: "0.4rem",
      fontWeight: "bold",
      color: "black",
      letterSpacing: "normal",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#b9baeb85",
    borderWidth: "2px",
  },
};

const compactFieldSxError = {
  ...compactFieldSx,
  "& .MuiInputLabel-root": {
    ...compactFieldSx["& .MuiInputLabel-root"],
    "&.MuiInputLabel-shrink": {
      ...(compactFieldSx["& .MuiInputLabel-root"] as any)["&.MuiInputLabel-shrink"],
      color: "#d32f2f",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d32f2f",
    borderWidth: "2px",
  },
};

// ─── CompactSelect ────────────────────────────────────────────────────────────
const CompactSelect: React.FC<CompactSelectProps> = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
  sx,
  slotProps,
  onBlur,
  error = false,
  helperText,
}) => {
  const sortedOptions = [...options].sort((a, b) =>
    a.label.localeCompare(b.label)
  );
  const dynamicLabelProps = value
    ? { shrink: true, ...slotProps?.inputLabel }
    : slotProps?.inputLabel || {};

  return (
    <FormControl
      fullWidth
      size="small"
      error={error}
      disabled={disabled}
      sx={{ ...(error ? compactFieldSxError : compactFieldSx), ...sx }}
    >
      <InputLabel {...dynamicLabelProps}>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={onChange}
        onBlur={onBlur}
        displayEmpty
        sx={{ "& .MuiSelect-select": { py: 0.5, color: value ? "inherit" : "#999" } }}
      >
        {!value && (
          <MenuItem value="" disabled sx={{ fontSize: "0.7rem", color: "#999" }}>
            Select
          </MenuItem>
        )}
        {sortedOptions.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: "0.7rem" }}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText sx={{ fontSize: "0.65rem", margin: "3px 0 0 0" }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

// ─── CompactTextField ─────────────────────────────────────────────────────────
const CompactTextField: React.FC<CompactTextFieldProps> = ({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  sx,
  slotProps,
  error = false,
  helperText,
  onBlur,
  multiline = false,
  rows = 1,
  placeholder,
}) => {
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      type === "number" &&
      (event.key === "e" || event.key === "E" || event.key === "+")
    ) {
      event.preventDefault();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (type === "number") {
      if (/^-?\d*\.?\d*$/.test(inputValue) || inputValue === "") {
        onChange(event);
      }
    } else {
      onChange(event);
    }
  };

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
      type={type}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      placeholder={placeholder}
      InputLabelProps={{
        shrink: type === "date" ? true : slotProps?.inputLabel?.shrink,
      }}
      InputProps={{
        onKeyPress: handleKeyPress,
        inputMode: type === "number" ? "numeric" : undefined,
        sx: { fontSize: "0.7rem", height: multiline ? "auto" : "32px" },
      }}
      sx={{
        ...(error ? compactFieldSxError : compactFieldSx),
        "& .MuiInputBase-root": {
          fontSize: "0.7rem",
          height: multiline ? "auto" : "32px",
          fontWeight: "bold",
          marginBottom: "0.5rem",
        },
        ...sx,
      }}
    />
  );
};

// ─── Main Form Component ──────────────────────────────────────────────────────
const CostObjectForm: React.FC<any> = ({ dataVersion, setDataVersion, copiedData, viewMode, setViewMode, groupByDueDate, setGroupByDueDate, formFilters, setFormFilters,
  totalCashIn, totalCashOut, totalNet, }) => {
  const queryClient = useQueryClient();
  const [isFormModified, setIsFormModified] = useState(false);
  const [touched, setTouched] = useState<TouchedType>({});
  const [errors, setErrors] = useState<ErrorsType>({});

  // Dropdown options — swap mock arrays with API calls when ready
  const [bookOptions, setBookOptions] = useState<OptionType[]>([]);

  // const [bookOptions, setBookOptions] = useState<OptionType[]>(MOCK_BOOKS);
  const [traderOptions, setTraderOptions] = useState<OptionType[]>(MOCK_TRADERS);
  const [strategyOptions, setStrategyOptions] = useState<OptionType[]>(MOCK_STRATEGIES);
  const [clearerOptions, setClearerOptions] = useState<OptionType[]>(MOCK_CLEARERS);
  const [brokerOptions, setBrokerOptions] = useState<OptionType[]>(MOCK_BROKERS);
  const [productOptions, setProductOptions] = useState<OptionType[]>(MOCK_PRODUCTS);
  const [counterpartyOptions, setCounterpartyOptions] = useState<OptionType[]>(MOCK_COUNTERPARTIES);
  const [currencyOptions, setCurrencyOptions] = useState<OptionType[]>(MOCK_CURRENCIES);
  // const [costTypeOptions, setCostTypeOptions] = useState<OptionType[]>(
  //   mockCostTypeData.map((item) => ({
  //     value: item.name.toLowerCase().replace(/\s+/g, "_"),
  //     label: item.name,
  //   }))
  // );
  // Add inside CostObjectForm component, before the return
  const activeFilterLabels: { field: string; label: string; value: string }[] = [];

  const filterFieldLabels: Record<string, string> = {
    book: "Book",
    strategy: "Strategy",
    trader: "Trader",
    clearer: "Clearer",
    broker: "Broker",
    product: "Product",
    counterparty: "Counterparty",
    currency: "Currency",
  };

  const allFilterOptions: Record<string, OptionType[]> = {
    book: bookOptions,
    strategy: strategyOptions,
    trader: traderOptions,
    clearer: clearerOptions,
    broker: brokerOptions,
    product: productOptions,
    counterparty: counterpartyOptions,
    currency: currencyOptions,
  };

  Object.entries(formFilters).forEach(([field, val]: [string, any]) => {
    if (!val) return;
    const options = allFilterOptions[field] || [];
    const match = options.find((o: OptionType) => o.value === val);
    activeFilterLabels.push({
      field,
      label: filterFieldLabels[field] || field,
      value: (match?.label || val) as string,
    });
  });
  const hasActiveFilters = activeFilterLabels.length > 0;

  const handleClearAllFilters = () => {
    setFormFilters({
      book: "", strategy: "", trader: "", clearer: "",
      broker: "", product: "", counterparty: "", currency: "",
    });
  };

  const [formData, setFormData] = useState<FormDataType>({
    type: "lump_sum",
    costType: "",
    amount: "",
    currency: "USD",
    dueDate: "",
    book: "",
    trader: "",
    strategy: "",
    clearer: "",
    broker: "",
    product: "",
    counterparty: "",
    requiresSettlement: "",
    excludeFromPNL: "",
    description: "",
    status: "pending",
  });

  // TODO: Replace mock data with API calls when endpoints are ready.
  // Example pattern (same as contract page):
  // const fetchBookOptions = async () => {
  //   const response = await axiosInstance.get(`${backendbaseUrl}/paper-app/books/`);
  //   setBookOptions(response.data.results.map((item: any) => ({ value: item.id, label: item.name })));
  // };
  // useEffect(() => {
  //   fetchBookOptions();
  //   fetchTraderOptions();
  //   fetchStrategyOptions();
  //   fetchClearerOptions();
  //   fetchBrokerOptions();
  //   fetchProductOptions();
  //   fetchCounterpartyOptions();
  //   fetchCurrencyOptions();
  //   fetchCostTypeOptions();
  // }, [dataVersion]);

  // ─── Fetch real dropdown data (same APIs as CompactForm) ──────────────────
  useEffect(() => {
    // Books
    fetchBookOptions().then(({ data, error }) => {
      if (!error && data) {
        const nameBasedOptions = data.map((opt: any) => ({
          value: opt.label,
          label: opt.label,
        }));
        setBookOptions(nameBasedOptions);
      }
    });
    axiosInstance.get(`${backendbaseUrl}/paper-app/brokers/`)
      .then((response) => {
        const brokers = response.data?.results || response.data || [];
        setBrokerOptions(
          brokers.map((item: any) => ({
            value: item.name,   // use name as value to match DAILY_DUMMY broker strings
            label: item.name,
          }))
        );
      })
      .catch(() => {
        // fallback to mock on error
      });

    // Strategy
    // StrategyOptions().then(({ data, error }) => {
    //   if (!error && data) setStrategyOptions(data);
    // });

    // Clearer
    fetchClearerName().then(({ data, error }) => {
      if (!error && data) {
        const filtered = data.filter((opt: any) => opt.value !== "__create__new");
        setClearerOptions(
          filtered.map((opt: any) => ({
            value: opt.label ?? opt.clearer,  // use label as value too
            label: opt.label ?? opt.clearer
          }))
        );
      }
    });
  }, [dataVersion]);
  // useEffect(() => {
  //   setCostTypeOptions(
  //     mockCostTypeData.map((item) => ({
  //       value: item.name.toLowerCase().replace(/\s+/g, "_"),
  //       label: item.name,
  //     }))
  //   );
  // }, [dataVersion]);
      axiosInstance.get(`${backendbaseUrl}/paper-app/counterparty-companies/`)
        .then((res) => {
          const results = res.data?.results ?? res.data ?? [];
          setCounterpartyOptions(
        results
          .map((item: any) => ({
            value: item.id ?? "",
            label: item.customer_company_name ?? "",  // ← correct field name from your API
          }))
          .filter((opt: any) => opt.label)  // ← guards against the localeCompare crash
      );
        })
        .catch(() => { });
  
  

  useEffect(() => {
    if (formData.product) {
      fetchBrokerNameOptions(formData.product).then(({ data, error }) => {
        if (!error && data) setBrokerOptions(data);
      });
    }
  }, [formData.product]);

  useEffect(() => {
    console.log("useEffect copiedData", copiedData);
    if (!copiedData) return;

    const map: Partial<FormDataType> = {
      costType: copiedData.cost_type || "",
      amount: copiedData.amount != null ? copiedData.amount.toString() : "",
      currency: copiedData.currency || "USD",
      dueDate: copiedData.due_date || "",
      book: copiedData.book || "",
      trader: copiedData.trader || "",
      strategy: copiedData.strategy || "",
      clearer: copiedData.clearer || "",
      broker: copiedData.broker || "",
      product: copiedData.product || "",
      counterparty: copiedData.counterparty || "",
      requiresSettlement: copiedData.requires_settlement === false ? "no" : "yes",
      excludeFromPNL: copiedData.exclude_from_pnl === true ? "yes" : "no",
      description: copiedData.description || "",
      status: copiedData.status || "pending",
    };

    const updatedFormData = { ...formData };
    Object.entries(map).forEach(([key, value]) => {
      if (value !== null && value !== "") {
        updatedFormData[key as keyof FormDataType] = value as string;
      }
    });

    setFormData(updatedFormData);
    setIsFormModified(true);
    setTouched({});
    setErrors({});
  }, [copiedData]);

  // ─── Validation ─────────────────────────────────────────────────────────────
  const requiredFields: (keyof FormDataType)[] = [
    "costType", "amount", "currency", "book", "trader",
    "strategy", "clearer", "broker", "product", "counterparty",
  ];

  const fieldLabels: Record<string, string> = {
    costType: "Cost Type",
    amount: "Amount",
    currency: "Currency",
    book: "Book",
    trader: "Trader",
    strategy: "Strategy",
    clearer: "Clearer",
    broker: "Broker",
    product: "Product",
    counterparty: "Counterparty",
  };

  const validateField = (field: keyof FormDataType, value: string): string => {
    if (requiredFields.includes(field) && (!value || value.trim() === "")) {
      return `${fieldLabels[field as string] || field} is required`;
    }
    if (field === "amount" && value && isNaN(Number(value))) {
      return "Amount must be a valid number";
    }
    if (field === "amount" && value && Number(value) === 0) {
      return "Amount cannot be zero";
    }
    return "";
  };

  const validateAllFields = (): boolean => {
    const newErrors: ErrorsType = {};
    let isValid = true;
    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field as string] = error;
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleBlur = (field: keyof FormDataType) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleTextChange =
    (field: keyof FormDataType) => (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setFormData({ ...formData, [field]: newValue });
      setIsFormModified(true);
      const error = validateField(field, newValue);
      setErrors((prev) => ({ ...prev, [field]: error }));
    };

  const handleSelectChange =
    (field: keyof FormDataType) => (event: SelectChangeEvent) => {
      const newValue = event.target.value;
      setFormData({ ...formData, [field]: newValue });
      setIsFormModified(true);
      if (errors[field as string]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

  const handleRadioChange =
    (field: keyof FormDataType) => (event: ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: event.target.value });
      setIsFormModified(true);
    };

  const generateId = () =>
    `CO-${Date.now()}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

  const resetFormData = () => {
    setFormData({
      type: "lump_sum",
      costType: "",
      amount: "",
      currency: "USD",
      dueDate: "",
      book: "",
      trader: "",
      strategy: "",
      clearer: "",
      broker: "",
      product: "",
      counterparty: "",
      requiresSettlement: "yes",
      excludeFromPNL: "no",
      description: "",
      status: "pending",
    });
    setTouched({});
    setErrors({});
    setIsFormModified(false);
  };

  const handleClearForm = () => resetFormData();

  const SubmitFormdata = async (e: any) => {
    e.preventDefault();

    const allTouched: TouchedType = {};
    requiredFields.forEach((field) => {
      allTouched[field as string] = true;
    });
    setTouched(allTouched);

    if (!validateAllFields()) {
      toast.error("Please complete all required fields before submitting");
      return;
    }

    const payload = {
      id: generateId(),
      status: formData.status,
      type: formData.type,
      cost_type: formData.costType,
      amount: Number(formData.amount),
      currency: formData.currency,
      due_date: formData.dueDate || null,
      book: formData.book,
      trader: formData.trader,
      strategy: formData.strategy,
      clearer: formData.clearer,
      broker: formData.broker,
      product: formData.product,
      counterparty: formData.counterparty,
      requires_settlement: formData.requiresSettlement === "yes",
      exclude_from_pnl: formData.excludeFromPNL === "yes",
      description: formData.description || null,
      created_at: new Date().toISOString(),
    };


    // TODO: Replace with real API call when endpoint is ready:
    // try {
    //   const response = await axiosInstance.post(`${backendbaseUrl}/paper-app/cost-objects/`, payload);
    //   if ([200, 201, 202, 203].includes(response.status)) {
    //     toast.success("✅ Cost Object created successfully!");
    //     setDataVersion(dataVersion + 1);
    //     resetFormData();
    //   }
    // } catch (error: any) {
    //   const apiError = error.response?.data;
    //   if (apiError?.errors?.non_field_errors?.length > 0) {
    //     toast.error(apiError.errors.non_field_errors[0]);
    //   } else if (apiError?.message) {
    //     toast.error(apiError.message);
    //   } else {
    //     toast.error("Something went wrong. Please try again.");
    //   }
    // }

    // Mock store (remove when API is live)
    try {
      const STORAGE_KEY = "cost_objects_mock_data";
      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      existing.push(payload);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    } catch {
      /* ignore */
    }

    toast.success("✅ Cost Object created successfully!");
    setDataVersion(dataVersion + 1);
    resetFormData();
  };

  return (
    <Accordion defaultActiveKey="0" className="table-ad__addInputs">
      <Accordion.Item eventKey="0">
        <Accordion.Header className="accordion_button">
          Cash Activity
        </Accordion.Header>
        <Accordion.Body>
          <Box
            sx={{
              width: "100%",
              p: { xs: 1, sm: 1 },
              pt: "20px !important",
              backgroundColor: "#ffff",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
              position: "relative",
            }}
          >
            {/* ── ID & Status row — shown above Type/CostType ── */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                mb: 0.5,
                marginLeft: "8px",
                "& > *": {
                  flex: "1 1 0px",
                  minWidth: {
                    xs: "100%",
                    sm: "48%",
                    md: "23%",
                    lg: "15%",
                    xl: "7.5%",
                  },
                },
              }}
            >
              <FormControl component="fieldset">
                <FormLabel
                  component="legend"
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "#333", mb: 0.25 }}
                >
                  View
                </FormLabel>
                <RadioGroup
                  row
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as "daily" | "monthly")}
                >
                  <FormControlLabel
                    value="daily"
                    control={<Radio size="small" sx={{ py: 0.25 }} />}
                    label={<span style={{ fontSize: "0.75rem" }}>Daily</span>}
                  />
                  <FormControlLabel
                    value="monthly"
                    control={<Radio size="small" sx={{ py: 0.25 }} />}
                    label={<span style={{ fontSize: "0.75rem" }}>Monthly</span>}
                  />
                </RadioGroup>
              </FormControl>
              <Button
                variant="contained"
                onClick={() => setGroupByDueDate((prev: boolean) => !prev)}
                sx={{
                  backgroundColor: groupByDueDate ? "#0077b6" : "#00bcd4",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  height: "32px",
                  alignSelf: "center",
                  width: "fit-content",
                  flex: "0 0 auto",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: groupByDueDate ? "#005f8e" : "#008c9e",
                  },
                }}
              >
                {groupByDueDate ? "Ungroup Due Date" : "Group By Due Date"}
              </Button>
            </Box>
            {hasActiveFilters && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 0.75,
                  px: 0.5,
                  py: 0.5,
                  backgroundColor: "#f0f4ff",
                  borderRadius: "6px",
                  border: "1px solid #d0d9f5",
                }}
              >
                <span
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#444",
                    marginRight: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  Filters (Active)
                </span>
                {activeFilterLabels.map(({ field, label, value }) => (
                  <Chip
                    key={field}
                    label={
                      <span style={{ fontSize: "0.7rem" }}>
                        <span style={{ fontWeight: 700, color: "#1a237e" }}>{label}: </span>
                        <span style={{ color: "#333" }}>{value}</span>
                      </span>
                    }
                    onDelete={() =>
                      setFormFilters((prev: any) => ({ ...prev, [field]: "" }))
                    }
                    size="small"
                    sx={{
                      height: "22px",
                      backgroundColor: "#fff",
                      border: "1px solid #9fa8da",
                      borderRadius: "12px",
                      "& .MuiChip-deleteIcon": {
                        fontSize: "0.85rem",
                        color: "#e53935",
                        "&:hover": { color: "#b71c1c" },
                      },
                    }}
                  />
                ))}
                <Button
                  size="small"
                  onClick={handleClearAllFilters}
                  sx={{
                    ml: "auto",
                    fontSize: "0.7rem",
                    textTransform: "none",
                    color: "#e53935",
                    fontWeight: 700,
                    minWidth: "auto",
                    px: 1,
                    py: 0.25,
                    "&:hover": { backgroundColor: "#ffeaea" },
                  }}
                >
                  Clear All
                </Button>
              </Box>
            )}

            <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.5, mt: 0.5, "& > *": { flex: "1 1 0px", minWidth: 0 } }}>
              {[
                { label: "Book", field: "book" as const, options: bookOptions },
                { label: "Strategy", field: "strategy" as const, options: strategyOptions },
                { label: "Trader", field: "trader" as const, options: traderOptions },
                { label: "Clearer", field: "clearer" as const, options: clearerOptions },
              ].map(({ label, field, options }) => (
                <CompactSelect
                  key={field}
                  label={`${label} *`}
                  value={formFilters[field] || ""}
                  onChange={(e) => setFormFilters((prev: any) => ({ ...prev, [field]: e.target.value }))}
                  options={options}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              ))}
            </Box>


            {/* ── Row 2b: Broker, Product, Counterparty, Currency ── */}
            <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.5, mt: 0.5, "& > *": { flex: "1 1 0px", minWidth: 0 } }}>
              {[
                { label: "Broker", field: "broker" as const, options: brokerOptions },
                { label: "Product", field: "product" as const, options: productOptions },
                { label: "Counterparty", field: "counterparty" as const, options: counterpartyOptions },
                { label: "Currency", field: "currency" as const, options: currencyOptions },
              ].map(({ label, field, options }) => (
                <CompactSelect
                  key={field}
                  label={`${label} *`}
                  value={formFilters[field] || ""}
                  onChange={(e) => setFormFilters((prev: any) => ({ ...prev, [field]: e.target.value }))}
                  options={options}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                mt: 1,
                alignItems: "flex-start",
              }}
            >
              <CompactTextField
                label="Start Date"
                value={formData.dueDate}
                onChange={handleTextChange("dueDate")}
                type="date"
                slotProps={{ inputLabel: { shrink: true } }}
                onBlur={() => handleBlur("dueDate")}
              />
              <CompactTextField
                label="End Date"
                value={formData.dueDate}
                onChange={handleTextChange("dueDate")}
                type="date"
                slotProps={{ inputLabel: { shrink: true } }}
                onBlur={() => handleBlur("dueDate")}
              />


            </Box>

            <Box sx={{ mt: 1, display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                size="small"
                label="Total Cash In"
                value={totalCashIn > 0 ? `+${totalCashIn.toLocaleString()}` : totalCashIn === 0 ? "—" : totalCashIn.toLocaleString()}
                InputProps={{ readOnly: true, sx: { fontSize: "0.7rem", color: "#2e7d32", fontWeight: "bold" } }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160, "& .MuiInputBase-root": { color: "#2e7d32" } }}
              />
              <TextField
                size="small"
                label="Total Cash Out"
                value={totalCashOut < 0 ? `${Math.abs(totalCashOut).toLocaleString()}` : totalCashOut === 0 ? "—" : totalCashOut.toLocaleString()}
                InputProps={{ readOnly: true, sx: { fontSize: "0.7rem", color: "#c62828", fontWeight: "bold" } }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160, "& .MuiInputBase-root": { color: "#c62828" } }}
              />
              <TextField
                size="small"
                label="Net"
                value={totalNet >= 0 ? `+${totalNet.toLocaleString()}` : totalNet.toLocaleString()}
                InputProps={{ readOnly: true, sx: { fontSize: "0.7rem", fontWeight: "bold" } }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160, "& .MuiInputBase-root": { color: totalNet >= 0 ? "#2e7d32" : "#c62828" } }}
              />
            </Box>
          </Box>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default CostObjectForm;